import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  initiateCustomAuth,
  respondToCustomChallenge,
  handleSuccessfulLogin,
} from 'utils/aws/authService'
import { renderFormField } from 'utils/forms'
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  FormControl,
  Input,
  InputLabel,
} from '@mui/material'
import { SessionInformation } from 'admin/utils/SessionInformation'

export const OtpLogin = ({
  setStepIndicator = () => {},
  otpLoginMethod = 'email',
}) => {
  useEffect(() => {
    const loadUpMySession = async () => {
      const demoUserInput = await SessionInformation.getItem('demoUserInput')
      setUsername(demoUserInput?.[otpLoginMethod])
    }
    loadUpMySession()
  }, [otpLoginMethod])
  const [username, setUsername] = useState('')
  const [otp, setOtp] = useState('')
  const [session, setSession] = useState(null)
  const [errorMessage, setErrorMessage] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [step, setStep] = useState(1)
  const navigate = useNavigate()
  const [newCodeMessage, setNewCodeMessage] = useState('none')
  const disabledButton =
    !/\b\d{10}\b/.test(username) &&
    !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(username)
  const inputRef = useRef(null)

  useEffect(() => {
    if (step === 2 && inputRef.current) {
      inputRef.current.focus()
    }
  }, [step])
  const handleRequestOtp = async () => {
    setErrorMessage('')
    setSuccessMessage('')
    setIsLoading(true)
    try {
      const session = await initiateCustomAuth(username, otpLoginMethod)
      setSession(session)
      setStep(2) // Move to the OTP input step
      setStepIndicator('none')
    } catch (error) {
      console.error('Error requesting OTP', error)
      if (error.name === 'UserNotFoundException') {
        setErrorMessage(
          `User not found. Please check your ${
            otpLoginMethod === 'email' ? 'email address' : 'phone number'
          } or Create an Account below!`
        )
      } else if (error.name === 'NotAuthorizedException') {
        setErrorMessage(
          `User not found. Please check your ${
            otpLoginMethod === 'email' ? 'email address' : 'phone number'
          } or Create an Account below!`
        )
      } else if (error.name === 'TooManyRequestsException') {
        setErrorMessage('Too many attempts. Please try again later.')
      } else {
        setErrorMessage('An unexpected error occurred. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerifyOtp = async () => {
    setErrorMessage('')
    setSuccessMessage('')
    setIsLoading(true)
    try {
      const tokens = await respondToCustomChallenge(
        username,
        otp,
        session,
        otpLoginMethod
      )
      await handleSuccessfulLogin(tokens)
      const cleanedRedirection = window.location.search.replace(
        '?redirect_to=' + window.location.origin,
        ''
      )
      navigate(cleanedRedirection)
    } catch (error) {
      console.error('Error verifying OTP', error)
      if (error.name === 'NotAuthorizedException') {
        setErrorMessage('Incorrect verification code. Please try again.')
      } else if (error.name === 'CodeMismatchException') {
        setErrorMessage('Incorrect verification code. Please try again.')
      } else if (error.name === 'ExpiredCodeException') {
        setErrorMessage(
          'Your verification code has expired. Sending a new code.'
        )
      } else if (error.name === 'TooManyRequestsException') {
        setErrorMessage('Too many attempts. Please try again later.')
      } else {
        setErrorMessage('An unexpected error occurred. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleNewAuthRequest = async () => {
    setErrorMessage('')
    setSuccessMessage('')
    setIsLoading(true)
    try {
      const session = await initiateCustomAuth(username)
      setSession(session)
      setStep(2)
      setStepIndicator('none')
      setNewCodeMessage('block')
    } catch (error) {
      console.error('Error requesting OTP', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCodeKeydown = (e) => {
    if (e.key === 'Enter') {
      handleVerifyOtp()
    }
  }

  return (
    <>
      {step === 1 && (
        <Box>
          <Typography variant="h4" textAlign="center" color={'#262627'}>
            Welcome to PARCO
          </Typography>
          <Typography mt={2} mb={2} variant="body2" textAlign="center">
            Sign{' '}
            {window.location.search ===
            '?redirect_to=http://localhost:3000/onboarding-full?onboardingStep=pickYourPath'
              ? 'up'
              : 'in'}{' '}
            with your {otpLoginMethod === 'email' ? 'email' : 'phone number'}!
          </Typography>
          {errorMessage && (
            <Alert
              sx={{ '&.MuiPaper-root': { padding: '0px 16px' } }}
              severity="error"
            >
              {errorMessage}
            </Alert>
          )}
          {successMessage && (
            <Alert
              sx={{ '&.MuiPaper-root': { padding: '0px 16px' } }}
              severity="success"
            >
              {successMessage}
            </Alert>
          )}
          <Box mt={2} sx={{ '.MuiBox-root': { height: 'auto' } }}>
            {otpLoginMethod === 'email' &&
              renderFormField({
                autoFocus: false,
                intakeAttributeName: 'email',
                intakeAttribute: username,
                placeholder: 'johnsmith@gmail.com',
                label: 'Email',
                onChange: (email) => setUsername(email),
                inputType: 'email',
                handleNext: handleRequestOtp,
              })}

            {otpLoginMethod === 'phone' &&
              renderFormField({
                autoFocus: false,
                intakeAttributeName: 'phone',
                intakeAttribute: username,
                placeholder: '+1 (###) ###-####',
                label: 'Phone Number',
                onChange: (phone) => setUsername(phone),
                inputType: 'tel',
                handleNext: handleRequestOtp,
              })}
            <Button
              variant="contained"
              sx={{
                fontSize: '15px',
                padding: '10px 24px 9px',
                backgroundColor: '#344767',
                color: '#fff',
                fontWeight: 'normal',
                boxShadow: 'none',
                textTransform: 'none',
                width: '100%',
                marginTop: '16px',
                '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                  transform: 'none',
                  backgroundColor: '#293852',
                  boxShadow: 'none',
                },
                '&:disabled': {
                  boxShadow: 'none',
                  border: '1px solid #ccc',
                },
              }}
              disabled={disabledButton}
              onClick={handleRequestOtp}
              endIcon={
                isLoading && <CircularProgress size={20} color="inherit" />
              }
            >
              {isLoading ? 'Requesting Code...' : 'Continue'}
            </Button>
          </Box>
        </Box>
      )}

      {step === 2 && (
        <Box>
          <Typography
            display={newCodeMessage}
            mb={2}
            variant="h5"
            textAlign="center"
            color={'#262627'}
          >
            New Security Code is on the Way!
          </Typography>
          <Typography variant="h4" textAlign="center" color={'#262627'}>
            Enter Security Code
          </Typography>
          <Typography mt={1} mb={2} variant="body2" textAlign="center">
            Please check your {otpLoginMethod === 'email' ? 'email' : 'phone'}{' '}
            for a message with your code. Your code is 8 digits long.
          </Typography>
          {errorMessage && (
            <Alert
              sx={{ '&.MuiPaper-root': { padding: '0px 16px' } }}
              severity="error"
            >
              {errorMessage}
            </Alert>
          )}
          {successMessage && (
            <Alert
              sx={{ '&.MuiPaper-root': { padding: '0px 16px' } }}
              severity="success"
            >
              {successMessage}
            </Alert>
          )}
          <Box
            mt={2}
            mx="auto"
            sx={{
              input: { textAlign: 'center', fontSize: '20px', height: '2rem' },
            }}
          >
            <FormControl
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                position: 'relative',
                color: '#2370ED',
                width: '100%',
                minWidth: '100%',
                maxWidth: '100%',
              }}
            >
              <InputLabel
                htmlFor="otp"
                sx={{
                  color: '#75787B',
                  top: '16px',
                  background: '#fff',
                  padding: '0 5px',
                  marginLeft: '-5px',
                  '&.Mui-focused': {
                    color: '#2370ED',
                  },
                }}
                shrink
              >
                One-Time Code:
              </InputLabel>
              <Input
                id="otp"
                name="otp"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                required
                ref={inputRef}
                onChange={(e) => {
                  // Only allow numeric characters and limit to 8 digits
                  const value = e.target.value.replace(/\D/g, '').slice(0, 8)
                  setOtp(value)
                }}
                onKeyDown={handleCodeKeydown}
                value={otp}
                maxLength={8}
                placeholder="####-####"
                sx={{
                  textAlign: 'center',
                  fontSize: '20px',
                  height: '2rem',
                  width: '100%',
                  minWidth: '100%',
                  maxWidth: '100%',
                  padding: '8px',
                  border: '2px solid #ccc',
                  borderRadius: '4px',
                  '&:before': {
                    display: 'none',
                  },
                  '&:after': {
                    display: 'none',
                  },
                  '&:hover': {
                    borderColor: '#2370ED',
                  },
                  '&.Mui-focused': {
                    borderColor: '#2370ED',
                    borderWidth: '2px',
                  },
                  '& input': {
                    textAlign: 'center',
                    width: '100%',
                    minWidth: '100%',
                    maxWidth: '100%',
                  },
                }}
              />
            </FormControl>
          </Box>
          <Box mt={3} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              sx={{
                fontSize: '15px',
                padding: '10px 24px 9px',
                backgroundColor: '#fff',
                color: '#344767',
                fontWeight: '500',
                boxShadow: 'none',
                textTransform: 'none',
                width: '50%',
                '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                  transform: 'none',
                  backgroundColor: '#f9f9f9',
                  boxShadow: 'none',
                },
              }}
              onClick={handleNewAuthRequest}
              autoFocus
            >
              {errorMessage ? 'Request a new code?' : `Didn't get a code?`}
            </Button>
            <Button
              variant="contained"
              sx={{
                fontSize: '15px',
                padding: '10px 24px 9px',
                backgroundColor: '#344767',
                color: '#fff',
                fontWeight: 'normal',
                boxShadow: 'none',
                textTransform: 'none',
                width: '50%',
                '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                  transform: 'none',
                  backgroundColor: '#293852',
                  boxShadow: 'none',
                },
              }}
              onClick={handleVerifyOtp}
              autoFocus
              endIcon={
                isLoading && <CircularProgress size={20} color="inherit" />
              }
            >
              {isLoading ? 'Submitting...' : 'Continue'}
            </Button>
          </Box>
        </Box>
      )}
    </>
  )
}
